import React, { useState } from 'react';
import { Box, Button, Stack, Typography, } from '@mui/material';
import { setApplication } from 'api/application';
import { useSnackbar } from 'components/snackbar';
import { toTitleCase } from 'utils/Utils';

/**
 * ExternalJobStatus is a React component that is used to display a job's details and provides a button to confirm that the user has successfully applied for the job.
 * It takes the following props:
 * - onClose: A function that is called when the component should close.
 * - tab: A string indicating the current tab context.
 * - job: An object containing the job's details.
 * The component displays a card with the job's title and id, and two buttons: a button to return to the home page and a button to confirm the application.
 * If the user clicks on the "Yes Applied Successfully" button, the component will update the job status and show a notification.
 * If the user clicks on the "Couldn't Apply" button, the component will delete the pending application and close the dialog.
 * If the submission fails for any reason, an error notification is shown.
 */

const ExternalJobStatus = ({ onClose, tab, job }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDelete, setIsDelete] = useState(false);

  /**
   * Handles the submission of the application when the user confirms that they have successfully applied to the job.
   * Makes an API call to update the job status to "applied" and sets the "applied_at" field to the current date and time.
   * If the submission is successful, it will show a success notification and update the job status on the component.
   * If the submission fails, it will show an error notification.
   * If the job is not found, it will show an error notification.
   */
  const handleJobApplied = async () => {
    try {
      setIsSubmitting(true);
      if (job) {
        const jobId = tab === '2' ? job?._id : job?.job_id;
        const response = await setApplication({ jobId: jobId, status: "applied" });
        if (response?.success && response?.data != null) {
          const company = response?.data?.job_id?.company;
          const title = response?.data?.job_id?.title;
          enqueueSnackbar(`Successfully applied to ${toTitleCase(company)} for the ${title} position`, { variant: 'success' });
          if (tab === '3') {
            job.status = response?.data?.status;
            job.applied_at = response?.data?.applied_at;
          } else {
            job.application_status = response?.data?.status;
          }
          onClose('applied');
        } else {
          enqueueSnackbar(response?.message, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to submit the application. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  /**
   * Handles the deletion of a pending application when the user confirms that they want to cancel their application.
   * Makes an API call to delete the job application and sets the "isDelete" state to true.
   * If the deletion is successful, it will close the dialog.
   * If the deletion fails, it will show an error notification.
   * If the job is not found, it will show an error notification.
   */
  const handleDeletePendingApplication = async () => {
    setIsDelete(true);
    try {
      const jobId = tab === '2' ? job?._id : job?.job_id;
      const response = await setApplication({ jobId, type: 'delete' });

      const isSuccess = response?.success;
      const isDeleted = response?.data?.acknowledged || response?.data?.deletedCount > 0;

      if (isSuccess) {
        if (tab === '3' && isDeleted) onClose('delete');
        else onClose();
      }
    } catch (e) {
      console.error('Failed to delete application:', e);
    } finally {
      setIsDelete(false);
    }
  };

  return (
    <Box className="ext-job-dialog-wraper">
      <Stack justifyContent={'center'}>
        <Box sx={{ textAlign: 'center' }} mb={3}>
          <Typography className="title" mb={0.5}>
            External job apply
          </Typography>
          <Typography className="info-text">
            Were you able to apply for the job?
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column-reverse', md: 'row' }} alignItems={'center'} spacing={2.5}>
          <Button fullWidth variant="outlined" size="medium" onClick={handleDeletePendingApplication} disabled={isDelete}>
            Couldn't Apply
          </Button>
          <Button fullWidth variant="contained" size="medium" onClick={handleJobApplied} disabled={isSubmitting}>
            {isSubmitting ? 'Applied...' : 'Yes Applied Successfully'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
export default ExternalJobStatus;
